import React, { useState } from "react";
import PropTypes from "kf-proptypes/widgets";
import { Trans, t, defineMessage } from "@lingui/macro";

import { isEmpty } from "base/utils";
import { i18n } from "base/i18n";

import { ICON_URL } from "./const.js";
import { ICON_COLOR_MAPPING } from "./colors.js";
import { TAB_SIZES, Tab, Tabs } from "@widgets/navigation/tab/index.jsx";
import {
  ICON_SIZE,
  ICON_STROKE,
  KF_ICONS,
  KfIcon
} from "@widgets/common/components/primitives/kf_icon/index.jsx";
import { TOOLTIP_POSITION, Tooltip } from "@widgets/feedback/index.jsx";
import { LazyLoadComponent } from "@widgets/others/lazyloader/index.jsx";

import styles from "./iconpicker.css";

IconPicker.propTypes = {
  /**
   * openPicker is used to open Localfile upload
   */
  openPicker: PropTypes.func,
  /**
   * it will contains how many tabs need to render based consumer array data
   */
  tabs: PropTypes.arrayOf(PropTypes.string),
  /**
   * onSelect function will select,selected icon.
   */
  onSelect: PropTypes.func,
  /**
   * selectdColor will holds what color user is selected.
   */
  selectedColor: PropTypes.string,
  /**
   * selected indicates which icon user is selected
   */
  selected: PropTypes.shape({
    system: PropTypes.string,
    shape: PropTypes.string,
    emoji: PropTypes.string,
    custom: PropTypes.string
  }),
  /**
   * defaultTabId which will indicates which tab need to render like custom or shapes.
   */
  defaultTabId: PropTypes.string,
  /**
   * flowType indicates from which flow icon is updating
   */
  flowType: PropTypes.string,
  /**
   * onlyIcon will indicates, It will render only Icon.
   */
  onlyIcon: PropTypes.bool,
  /**
   * iconPath will indicates from which bucket icons need to render
   */
  iconPath: PropTypes.string,
  /**
   * uploadingFiles will be the icon which is currently uploading via custom option
   */
  uploadingFiles: PropTypes.arrayOf(PropTypes.object), //object is FileWrapper instace - can't import here due to cyclic dependency
  /**
   * function to cancel the currently uploading icon via custom option
   */
  onFileCancel: PropTypes.func,
  /**
   * function to remove the currently uploading icon via custom option
   */
  onFileRemove: PropTypes.func,
  /**
   * function to be executed once icon uploaded via custom option
   */
  onFileUploadDone: PropTypes.func,
  colorMapping: PropTypes.oneOf(Object.values(ICON_COLOR_MAPPING))
};

export const ICON_PICKER_TYPE = {
  system: {
    id: "system",
    Name: defineMessage`System`
  },
  shape: {
    id: "shape",
    Name: defineMessage`Icons`
  },
  emoji: {
    id: "emoji",
    Name: defineMessage`Emoji`
  },
  custom: {
    id: "custom",
    Name: defineMessage`Custom`
  }
};

export function IconPicker(props) {
  const {
    tabs = [ICON_PICKER_TYPE.shape.id, ICON_PICKER_TYPE.custom.id],
    colorMapping = ICON_COLOR_MAPPING,
    openPicker,
    onSelect,
    defaultTabId,
    selected,
    selectedColor,
    uploadingFiles,
    onFileRemove,
    onFileCancel,
    onFileUploadDone,
    onlyIcon = false,
    iconPath = ICON_URL
  } = props;

  const [activeTab, setActiveTab] = useState(defaultTabId || tabs[0]);

  function handleclick(e) {
    e.stopPropagation();
  }

  const onSelectTab = (activeTab) => {
    setActiveTab(activeTab);
  };

  const onSelectProperty = (data) => {
    // data will have {color, icon, emoji}
    onSelect(data);
  };

  let selectedValue = selected && selected[activeTab];

  return (
    <div onClick={handleclick}>
      <Tabs
        className={`${styles.iconUploadContainer} ${
          styles[`${activeTab}pickerTab`]
        }`}
        activeTabId={activeTab}
        size={TAB_SIZES.MEDIUM}
        onChange={(tabId) => {
          onSelectTab(tabId);
        }}
        rightAction={
          <Tooltip
            content={t`Reset to default`}
            position={TOOLTIP_POSITION.BOTTOM}
          >
            <div
              className={styles.reset}
              onClick={() => {
                onSelect({ isReset: true });
                setActiveTab(tabs[0]);
                onFileCancel();
              }}
            >
              <KfIcon
                icon={KF_ICONS.RESET}
                size={ICON_SIZE.SIZE_12}
                stroke={ICON_STROKE.THIN}
              />
              <Trans>Reset</Trans>
            </div>
          </Tooltip>
        }
      >
        {tabs.includes(ICON_PICKER_TYPE.shape.id) && (
          <Tab
            title={i18n._(ICON_PICKER_TYPE.shape.Name)}
            tabId={ICON_PICKER_TYPE.shape.id}
          >
            <LazyLoadComponent
              component={() =>
                import(/* webpackPreload: true */ "./pickers/shape/index.jsx")
              }
              componentName={"ShapePicker"}
              selectedIcon={selectedValue}
              selectedColor={selectedColor}
              onSelectIcon={(value) => onSelectProperty({ [activeTab]: value })}
              iconPath={iconPath}
              showColorPicker={!onlyIcon}
              colorMapping={colorMapping}
              onSelectColor={onSelectProperty}
            />
          </Tab>
        )}
        {tabs.includes(ICON_PICKER_TYPE.custom.id) && (
          <Tab
            title={i18n._(ICON_PICKER_TYPE.custom.Name)}
            tabId={ICON_PICKER_TYPE.custom.id}
          >
            <div className={styles.uploadContainer}>
              {!isEmpty(uploadingFiles) ? (
                <LazyLoadComponent
                  component={() => import("kfplatform/filePicker")}
                  componentName="AttachmentList"
                  uploadedFiles={uploadingFiles}
                  onFileRemove={onFileRemove}
                  onFileCancel={onFileRemove}
                  onFileUpload={onFileUploadDone}
                  filePreviewConfig={{
                    disableFilePreview: true
                  }}
                  hideDownload={true}
                />
              ) : (
                <div className={styles.upload} onClick={openPicker}>
                  <div className={styles.uploadTextContainer}>
                    <div className={styles.chooseContainer}>
                      <KfIcon
                        icon={KF_ICONS.ATTACH}
                        size={ICON_SIZE.SIZE_12}
                        stroke={ICON_STROKE.LIGHT}
                        color={`--kf-color-primary-500`}
                      />
                      <p className={`smallplus-600  ${styles.chooseText}`}>
                        <Trans>Choose file</Trans>
                      </p>
                    </div>
                    <p className={`micro-400 ${styles.descText}`}>
                      <Trans>Maximum file size: 5 MB</Trans>
                    </p>
                  </div>
                </div>
              )}
            </div>
          </Tab>
        )}
        {tabs.includes(ICON_PICKER_TYPE.emoji.id) && (
          <Tab
            title={i18n._(ICON_PICKER_TYPE.emoji.Name)}
            tabId={ICON_PICKER_TYPE.emoji.id}
          >
            <LazyLoadComponent
              component={() =>
                import(/* webpackPreload: true */ "./pickers/emoji/index.jsx")
              }
              componentName={"EmojiPicker"}
              onClick={(value) => onSelectProperty({ [activeTab]: value })}
            />
          </Tab>
        )}
      </Tabs>
    </div>
  );
}

IconPicker.defaultProps = {
  tabs: [ICON_PICKER_TYPE.shape.id, ICON_PICKER_TYPE.custom.id],
  colorMapping: ICON_COLOR_MAPPING,
  onlyIcon: false,
  systemIcon: <></>,
  iconPath: ICON_URL
};
